import {
  type CheckoutTokenTransferConfig,
  effectiveDepositTarget,
  isAddressCompatibleWithChain,
  isDirectFiatDelivery,
  useLogOnce,
} from '@funkit/connect-core'

import type {
  DepositClientMetadata,
  DepositTargetConfig,
  FiatRailConfig,
} from '../flow/depositFlowConfig'
import { isUsableWalletAddress } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import { useDepositRecipient } from '../transfer/hooks/useDepositRecipient'
import { useTokenTransfer } from '../transfer/hooks/useTokenTransfer'
import { useUDAParams } from '../transfer/hooks/useUDAParams'

export interface UseFiatDirectDeliveryParams {
  /** Destination chain/asset the deposit is headed for. */
  checkoutConfig: DepositTargetConfig | undefined
  rail: FiatRailConfig
  dynamicRoutingId: string | undefined
  checkoutId: string
  clientMetadata: DepositClientMetadata | undefined
  /** Host fallback for local/dev clients that mint no address of their own. */
  depositAddressOverride?: string
  /** The verified assertion supplies the recipient on the backend. */
  resolveOnBackend?: boolean
}

export interface UseFiatDirectDeliveryResult {
  /** Where the order delivers the crypto; `undefined` until one resolves. */
  depositAddress: string | undefined
  /** The rail buys the destination asset itself, so no address was minted. */
  isDirect: boolean
  /** Why no generated address resolved; always `undefined` on the direct route. */
  depositAddressError: Error | undefined
  /** Re-requests the generated address; a no-op on the direct route. */
  retryDepositAddress: () => void
}

/**
 * Resolves where a V2 fiat order delivers.
 *
 * The generated deposit address exists to swap what the rail bought into what
 * the user asked for. When the rail buys that asset outright it has nothing to
 * do, so the order names the recipient instead — the crypto lands where it is
 * wanted rather than at a per-checkout address the backend has still to sweep.
 */
export function useFiatDirectDelivery({
  checkoutConfig,
  rail,
  dynamicRoutingId,
  checkoutId,
  clientMetadata,
  depositAddressOverride,
  resolveOnBackend = false,
}: UseFiatDirectDeliveryParams): UseFiatDirectDeliveryResult {
  // The config is not the last word on where a deposit lands: dynamic routing
  // re-targets it, and a route carrying an action has work the rail cannot do,
  // so neither may be skipped on the raw target.
  const udaParams = useUDAParams(dynamicRoutingId)
  // connect-core types the target as an EVM address; the direct-delivery check
  // only compares chain + address strings, so a Solana mint passes through.
  const target = effectiveDepositTarget({
    checkoutConfig: checkoutConfig as CheckoutTokenTransferConfig | undefined,
    udaParams,
  })
  const buysWhatIsWanted =
    target?.actionType === undefined &&
    isDirectFiatDelivery({
      destination: target,
      rail: { chainId: rail.chainId, address: rail.cryptoCurrencyAddress },
    })

  // Resolved before the address query rather than read off it: whether an
  // address is minted at all depends on who is receiving, so a recipient that
  // arrived with the answer would come too late to ask for one.
  const { recipient } = useDepositRecipient({
    checkoutConfig,
    udaTargetChain: udaParams?.targetChain,
  })
  // The rail pays one chain, so an address of another chain's shape cannot
  // receive what it buys — an EVM recipient can never take delivery of a
  // Solana order, however exactly the assets match.
  const deliversToRecipient =
    buysWhatIsWanted &&
    isUsableWalletAddress(recipient) &&
    isAddressCompatibleWithChain(recipient, Number(rail.chainId))

  const {
    depositAddress: generatedDepositAddress,
    depositAddressError,
    retryDepositAddress,
  } = useTokenTransfer({
    checkoutConfig,
    selectedChainId: Number(rail.chainId),
    dynamicRoutingId,
    checkoutId,
    clientMetadata,
    enabled: !resolveOnBackend && !deliversToRecipient,
  })

  // Money-path decision: it names where the provider sends the crypto.
  useLogOnce(`${resolveOnBackend}:${deliversToRecipient}`, () => {
    let route = 'backend'
    if (!resolveOnBackend) {
      route = deliversToRecipient ? 'recipient' : 'depositAddress'
    }
    logger.info('connect-rn:fiat:deposit_route', {
      route,
      chainId: rail.chainId,
      asset: rail.cryptoCurrencyAddress,
      targetChain: target?.chainId,
      targetAsset: target?.address,
    })
  })

  if (resolveOnBackend) {
    return {
      depositAddress: undefined,
      isDirect: true,
      depositAddressError: undefined,
      retryDepositAddress: () => {},
    }
  }

  return {
    depositAddress: deliversToRecipient
      ? recipient
      : (generatedDepositAddress ?? depositAddressOverride),
    isDirect: deliversToRecipient,
    depositAddressError: deliversToRecipient ? undefined : depositAddressError,
    retryDepositAddress,
  }
}
